import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import { useRouter, useRoute } from 'vue-router';
import { uuid } from 'vue-uuid';

import ConstUtils from '@/utils/ConstUtils.js';
import EventUtils from '@/utils/EventUtils.js';
import ContentUtils from "@/utils/ContentUtils.js";
import StringUtils from '@/utils/StringUtils.js';
import UserEvents from '@/domain/model/user/UserEvents.js';
import EventsDomain from '@/domain/model/events/EventsDomain.js';
import MC from "@/domain/session/MC.js";
import Company from "@/domain/model/company/Company.js";
import WebLogger from "@/domain/logger/WebLogger.js";
import BrowserStorage from '@/storage/BrowserStorage';
import RcDomain from '@/domain/RcDomain.js';

export function useConnection() {
  const store = useStore();
  const router = useRouter();
  const route = useRoute();

  const isLoading = ref(false);
  const loadingMessage = ref('');
  const showSessionTimeoutDialog = ref(false);
  const canStart = ref(false);

  const log = new WebLogger(window.console, true);

  const domain = computed(() => store.getters.domain);
  const auth_client = computed(() => store.getters.auth_client);
  const auth_connected = computed(() => store.getters.auth_connected);
  const auth_socket_status = computed(() => store.getters.auth_socket_status);
  const lastEvent = computed(() => store.getters.auth_socket_message);
  const currentCompany = computed(() => store.getters.AuthStorage_Company);

  const start = () => {
    canStart.value = true;
  }

  const connectSocket = () => {
    store.dispatch('connectSocket')
  }

  const callback = (message) => {
    loadingMessage.value = message;
  }

  const ensureConnection = () => {
    isLoading.value = true;
    var mc = new MC();
    loadingMessage.value = mc.Status.Loading.value();
    if (!isConnected()) {
      showSignIn();
    }
  }

  const isConnected = () => {
    var mc = new MC();
    var logger = new WebLogger(window.console, true);
    if (domain.value && auth_connected.value) {
      let session = domain.value.session();
      let company = session.company();
      let operatorId = ContentUtils.baseDecode(route.params.operatorId || '');
      if (operatorId != null && company.id() != operatorId) {
        let newCompany = domain.value.companys().findById(operatorId);
        if (newCompany.isNotNew()) {
          session.withCompany(newCompany);
          session.companyName = newCompany.name();
          store.dispatch('AuthStorage_updateCompany', newCompany);
        }
      }
      logger.info("Already connected: auth_connected starting page");
      return true;
    } else {
      var value = window.localStorage.readyChek;
      if (!value) {
        return false;
      }
      var cookieData = JSON.parse(value);
      if (!cookieData) {
        return false
      }
      callback(mc.Status.Connecting.value());
      logger.info("Attempting to create connection");
      connectSocket();
    }
    return true;
  }

  const showSignIn = () => {
    router.replace({ name: route.query.redirectTo || ConstUtils.ROUTES.SIGNIN });
  }

  const createDomainSession = () => {
    createDomain();
    createSession();
    start();
  }

  const createAdminDomainSession = () => {
    let domain = createDomain();
    createSession();
    if (StringUtils.cic(route.path, "admin/accounts")) {
      let encodedCompanyId = route.params.operatorId;
      let operatorId = ContentUtils.baseDecode(encodedCompanyId);
      callback(StringUtils.format("Loading Company {0}", [operatorId]));

      let context = {};
      context['domain'] = domain;
      var event = domain.events().company().details(operatorId);
      event.send(loadedCompanyListener, context);
    } else {
      start();
    }
  }

  const loadedCompanyListener = (notification, context) => {
    context["completed"] = new Date();
    let domain = context["domain"];
    var payload = notification.payload();
    var data = payload.valuesFor(Company.MODEL_NAME);
    var company = new Company(domain, data);

    domain.session().withCompany(company);
    domain.session().companyName = company.name();
    store.dispatch('AuthStorage_updateCompany', company);
    start();
  };

  const displayStatus = () => {
    if (!auth_connected.value) {
      showSessionTimeoutDialog.value = true;
    } else {
      status(auth_socket_status.value);
    }
  }

  const sendSignInEvent = () => {
    if (!auth_connected.value) {
      showSessionTimeoutDialog.value = true;
    } else {
      createConnection();
    }
  }

  const withAdminCompany = () => {
    var company = new Company(domain.value);
    company.withName("Administration Portal");
    domain.value.session().with(company);
    store.dispatch('AuthStorage_updateCompany', company);
  }

  const createConnection = () => {
    var mc = new MC();
    var logger = new WebLogger(window.console, true);
    callback(mc.Status.Connecting.value());
    var value = window.localStorage.readyChek;
    var cookieData = {};
    if (value) {
      cookieData = JSON.parse(value);
    }
    logger.info("Sending signin event");
    let password = ContentUtils.decodeText(cookieData.password)
    const requestEvent = UserEvents.SignIn.Request(cookieData.email, password);
    requestEvent[ConstUtils.GRECAPTCHA.NAME] = '';
    requestEvent['maker'] = window.navigator.userAgent;
    EventsDomain.initEvent(
      requestEvent, null,
      uuid.v1(),
      auth_client.value.deviceId,
      auth_client.value.version);
    EventUtils.Send.event(auth_client.value.socket.connection, requestEvent);
  }

  const createDomain = () => {
    var mc = new MC();
    var logger = new WebLogger(window.console, true);
    var storage = new BrowserStorage(store.getters);
    callback(mc.Status.CreatingSession.value());
    logger.info("Creating domain");
    const mockPanel = {
      auth_client: auth_client.value,
      addListenerAction: (listener) => store.dispatch('addListenerAction', listener),
      removeListenerAction: (listener) => store.dispatch('removeListenerAction', listener),
      updateDomain: (newDomain) => store.dispatch('updateDomain', newDomain),
      $store: store,
      $connect: () => store.dispatch('connectSocket'),
    };
    var domain = new RcDomain(mockPanel, storage);
    domain.withStorage(store.getters).withLogger(window.console).start();
    domain.events().withListeners(mockPanel.addListenerAction, mockPanel.removeListenerAction);
    store.dispatch('updateDomain', domain);
    callback("Done");
    return domain;
  }

  const createSession = () => {
    var session = domain.value.session();
    var logger = new WebLogger(window.console, true);
    var value = window.localStorage.readyChek;
    var cookieData = {};

    if (value) {
      cookieData = JSON.parse(value);
    }
    var companyId = cookieData['Company'];
    var userId = cookieData['User'];
    var user = session.user();
    if (user.isNew()) {
      user = domain.value.users().findById(userId);
    }
    var employees = domain.value.employees().findByUserId(user.id());

    if (StringUtils.isEmpty(companyId)) {
      const roles = employees.roles().find();
      if (roles.isAdministrator()) {
        session.with(employees.administrator());
        withAdminCompany();
        return session;
      }
      router.replace({ name: route.query.redirectTo || ConstUtils.ROUTES.SIGNIN });
      return session;
    }
    var employee = employees.findByCompanyId(companyId);
    if (employees.length() > 1 && employee.isNew()) {
      router.replace({ name: route.query.redirectTo || ConstUtils.ROUTES.COMPANY.SELECT });
    } else if (employees.length() == 0) {
      showSignIn();
    } else {
      var company = employee.company().find();
      logger.info("User: {0}", [employee.user().find().fullName()]);
      logger.info("Employee for company: {0}", [company.name()]);
      session.with(company);
      session.with(employee);
      store.dispatch('AuthStorage_updateCompany', company);
      var roles = session.employee().roles().find();
      if (roles.isAdministrator()) {
        withAdminCompany();
      }
    }
    return session;
  }

  const status = (status) => {
    var mc = new MC();
    if (!status) {
      callback(mc.Status.Loading.value());

    } else {
      if (status == 'findingYou') {
        callback(mc.Status.FindingYou.value());
      } else if (status == 'findingSchedules') {
        callback(mc.Status.FindingSchedules.value());
      } else if (status == 'findingInspections') {
        callback(mc.Status.FindingInspections.value());
      } else if (status == 'findingTimesheets') {
        callback(mc.Status.FindingHoursOfService.value());
      } else {
        callback(mc.Status.StoringData.value())
      }
    }
  }

  const adminInit = () => {
    isLoading.value = true;
    if (domain.value) {
      var session = domain.value.session();
      session.companyName = null;
      session.withoutCompany();
    }
  }

  const loadCompany = () => {
    var mc = new MC();
    var company = domain.value.session().company();
    let companyId = ContentUtils.baseDecode(route.params.operatorId || '');
    var fetch = company.id() != companyId;
    if (company.isPointer()) {
      fetch = true;
    }

    if (!companyId || !companyId.startsWith("#")) {
      fetch = false;
    }

    if (fetch) {
      isLoading.value = true;
      domain.value.session().companyName = null;
      loadingMessage.value = mc.Company.Status.Loading.value();
      var event = domain.value.events().company().details(companyId);
      event.send(loadCompanyListener);
    } else {
      isLoading.value = false;
    }
  }

  const adminLoadCompany = (company, pageName) => {
    if (company.isPointer()) {
      adminLoadCompanyRedirect(pageName);
    } else {
      let session = domain.value.session();
      session.with(company);
      session.companyName = company.name();
      store.dispatch('AuthStorage_updateCompany', company);
    }
  }

  const adminLoadCompanyRedirect = (pageName) => {
    let listPath = ConstUtils.ROUTES.SHARED.LOAD.COMPANY_PATH;
    router.push({
      path: listPath,
      query: {
        companyId: route.params.operatorId,
        page: pageName,
      }
    });
  }

  const adminIsAdministrator = () => {
    var user = domain.value.session().user();
    var employees = domain.value.employees().findByUserId(user.id());
    var roles = employees.roles().find();
    return roles.isAdministrator();
  }

  const ensureCompanyLoaded = (pageName) => {
    let companyId = ContentUtils.baseDecode(route.params.operatorId || '');
    if (adminIsAdministrator() && StringUtils.isNotEmpty(companyId)) {
      let company = domain.value.companies().findById(companyId);
      if (company.isNotNew()) {
        adminLoadCompany(company, pageName);
      } else {
        adminLoadCompanyRedirect(pageName);
      }
    }
  }

  const loadCompanyListener = (response) => {
    var payload = response.payload();
    var data = payload.valuesFor(Company.MODEL_NAME);
    var company = new Company(domain.value, data);

    domain.value.session().with(company);
    domain.value.session().companyName = company.name();
    store.dispatch('AuthStorage_updateCompany', company);
  }

  return {
    isLoading,
    loadingMessage,
    showSessionTimeoutDialog,
    canStart,
    log,
    start,
    ensureConnection,
    displayStatus,
    sendSignInEvent,
    createDomainSession,
    createAdminDomainSession,
    status,
    adminInit,
    loadCompany,
    adminIsAdministrator,
    ensureCompanyLoaded,
    lastEvent,
    currentCompany,
    withAdminCompany,
  }
}